import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import api from '../services/api'

/**
 * useNotificationStore - Zustand Store for Notifications
 *
 * Features:
 * - Fetch notifications from Backend API
 * - Unread count badge (P004: Badge Counter State Management)
 * - Mark as read (P005: Mark as Read Authentication)
 * - Real-time polling fallback (P002: HTTP Polling Protocol)
 * - Client-side event handling (P008)
 * - LocalStorage persistence
 *
 * Based on Father Mind's protocols and recommendations
 */

const useNotificationStore = create(
  persist(
    (set, get) => ({
      // ============================================
      // STATE
      // ============================================
      notifications: [],
      unreadCount: 0,
      loading: false,
      error: null,
      lastFetch: null,
      skip: 0,
      hasMore: true,
      pollInterval: null,

      // ============================================
      // FETCH NOTIFICATIONS
      // ============================================

      /**
       * Fetch all notifications with pagination
       * @param {number} skip - Number to skip (default: 0)
       */
      fetchNotifications: async (skip = 0) => {
        set({ loading: true, error: null })

        try {
          const response = await api.get('/mobile/notifications', {
            params: { skip }
          })

          if (response.data.status === 'success' && response.data.data) {
            const { notifications, count } = response.data.data

            set({
              notifications: skip === 0
                ? notifications
                : [...get().notifications, ...notifications],
              unreadCount: count || 0,
              loading: false,
              lastFetch: Date.now(),
              skip: skip,
              hasMore: notifications.length > 0
            })

            console.log('[Notifications] Fetched:', notifications.length, 'Unread:', count)
          }
        } catch (error) {
          console.error('[Notifications] Fetch error:', error)
          set({
            error: error.response?.data?.message || 'فشل تحميل الإشعارات',
            loading: false
          })
        }
      },

      /**
       * Fetch all notifications (read + unread)
       * Fetches up to 100 notifications for the full notifications page
       */
      fetchAllNotifications: async () => {
        set({ loading: true, error: null })

        try {
          // Request 100 notifications (max allowed by backend)
          const response = await api.get('/mobile/notifications?skip=0&count=100')

          if (response.data.status === 'success' && response.data.data) {
            const { notifications, count } = response.data.data

            set({
              notifications,
              unreadCount: count || 0,
              loading: false,
              lastFetch: Date.now()
            })

            console.log('[Notifications] All fetched:', notifications.length, 'Unread:', count)
          }
        } catch (error) {
          console.error('[Notifications] All fetch error:', error)
          set({
            error: error.response?.data?.message || 'فشل تحميل الإشعارات',
            loading: false
          })
        }
      },

      /**
       * Fetch unread notifications only
       */
      fetchUnreadNotifications: async () => {
        set({ loading: true, error: null })

        try {
          const response = await api.get('/mobile/notifications/unread')

          if (response.data.status === 'success' && response.data.data) {
            const { notifications, count } = response.data.data

            set({
              notifications,
              unreadCount: count || 0,
              loading: false,
              lastFetch: Date.now()
            })

            console.log('[Notifications] Unread:', count)
          }
        } catch (error) {
          console.error('[Notifications] Unread fetch error:', error)
          set({
            error: error.response?.data?.message || 'فشل تحميل الإشعارات',
            loading: false
          })
        }
      },

      /**
       * Load more notifications (pagination)
       */
      loadMore: async () => {
        const { skip, hasMore, loading } = get()
        if (loading || !hasMore) return

        await get().fetchNotifications(skip + 15)
      },

      // ============================================
      // MARK AS READ
      // ============================================

      /**
       * Mark single notification as read
       * @param {string} id - Notification ID
       */
      markAsRead: async (id) => {
        try {
          await api.post(`/mobile/notifications/${id}/read`)

          set(state => ({
            notifications: state.notifications.map(notif =>
              notif.id === id
                ? { ...notif, read_at: new Date().toISOString() }
                : notif
            ),
            unreadCount: Math.max(0, state.unreadCount - 1)
          }))

          console.log('[Notifications] Marked as read:', id)
        } catch (error) {
          console.error('[Notifications] Mark read error:', error)
        }
      },

      /**
       * Mark all notifications as read
       */
      markAllAsRead: async () => {
        try {
          await api.post('/mobile/notifications/read-all')

          set(state => ({
            notifications: state.notifications.map(notif => ({
              ...notif,
              read_at: notif.read_at || new Date().toISOString()
            })),
            unreadCount: 0
          }))

          console.log('[Notifications] All marked as read')
        } catch (error) {
          console.error('[Notifications] Mark all read error:', error)
        }
      },

      // ============================================
      // REAL-TIME UPDATES
      // ============================================

      /**
       * Add new notification (for real-time)
       * @param {Object} notification - Notification object
       */
      addNotification: (notification) => {
        set(state => ({
          notifications: [notification, ...state.notifications],
          unreadCount: state.unreadCount + 1
        }))

        // Browser notification if permission granted
        if ('Notification' in window && Notification.permission === 'granted') {
          new Notification(notification.data?.title || 'إشعار جديد', {
            body: notification.data?.message || '',
            icon: '/logo192.png',
            tag: notification.id
          })
        }
      },

      /**
       * Request browser notification permission
       */
      requestNotificationPermission: async () => {
        if ('Notification' in window && Notification.permission === 'default') {
          const permission = await Notification.requestPermission()
          return permission === 'granted'
        }
        return Notification.permission === 'granted'
      },

      // ============================================
      // POLLING (P002: HTTP Polling Protocol)
      // ============================================

      /**
       * Start polling for new notifications every 2 minutes
       */
      startPolling: () => {
        // Clear existing interval
        const existingInterval = get().pollInterval
        if (existingInterval) {
          clearInterval(existingInterval)
        }

        // Poll every 2 minutes
        const interval = setInterval(() => {
          get().fetchUnreadNotifications()
        }, 120000) // 2 minutes (120 seconds)

        set({ pollInterval: interval })
        console.log('[Notifications] Polling started (2 min interval)')
      },

      /**
       * Stop polling
       */
      stopPolling: () => {
        const interval = get().pollInterval
        if (interval) {
          clearInterval(interval)
          set({ pollInterval: null })
          console.log('[Notifications] Polling stopped')
        }
      },

      // ============================================
      // UTILITIES
      // ============================================

      /**
       * Get notification badge color based on type
       */
      getNotificationColor: (type) => {
        const colors = {
          invoice: 'blue',
          customer: 'green',
          product: 'purple',
          stock: 'orange',
          collection: 'indigo',
          return: 'red',
          warning: 'yellow',
          info: 'gray',
          success: 'emerald',
          error: 'red',
          transfer: 'teal'
        }
        return colors[type] || 'gray'
      },

      /**
       * Format relative time (منذ X دقيقة)
       */
      formatTime: (dateString) => {
        if (!dateString) return 'الآن'

        const date = new Date(dateString)
        const now = new Date()
        const diffMs = now - date
        const diffMins = Math.floor(diffMs / 60000)
        const diffHours = Math.floor(diffMs / 3600000)
        const diffDays = Math.floor(diffMs / 86400000)

        if (diffMins < 1) return 'الآن'
        if (diffMins < 60) return `منذ ${diffMins} دقيقة`
        if (diffHours < 24) return `منذ ${diffHours} ساعة`
        if (diffDays < 7) return `منذ ${diffDays} يوم`

        return date.toLocaleDateString('ar-EG', {
          day: 'numeric',
          month: 'short'
        })
      },

      /**
       * Clear local notifications (for testing)
       */
      clearLocal: () => {
        set({
          notifications: [],
          unreadCount: 0,
          error: null,
          skip: 0,
          hasMore: true
        })
        console.log('[Notifications] Cleared')
      },

      /**
       * Clear error
       */
      clearError: () => {
        set({ error: null })
      }
    }),
    {
      name: 'notification-storage',
      partialize: (state) => ({
        notifications: state.notifications,
        unreadCount: state.unreadCount,
        lastFetch: state.lastFetch
      })
    }
  )
)

export default useNotificationStore
